import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { checkDailyLogin, claimDailyWelcome, earnedPuzzles } from "@/lib/dailyLoginRewards";

export function DailyLoginRewards({ userId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(false);
  const refresh = useCallback(async () => {
    try {
      const result = await checkDailyLogin(api, userId);
      if (!alive.current) return;
      setData(result);
      setError(null);
      if (claimDailyWelcome(userId, result)) setOpen(true);
    } catch (e) {
      if (alive.current) setError(apiError(e));
    }
  }, [userId]);

  useEffect(() => {
    alive.current = true;
    refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive.current = false;
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const days = data?.days || 0;
  const rewards = data?.rewards || [];
  const pending = rewards.some(reward => !reward.puzzle_id);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button type="button" onClick={() => { setOpen(true); refresh(); }} data-testid="daily-login-open" className="font-mono-caps text-[10px] text-primary hover:underline">Daily</button>
      <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto" data-testid="daily-login-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">WELCOME BACK</DialogTitle>
          <DialogDescription>Open HHKD on seven consecutive days to earn a bonus puzzle. A missed day restarts your progress toward the next reward.</DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error} <button type="button" onClick={refresh} className="underline">Try again</button></p>}
        {!data && !error && <p role="status">Checking today's login…</p>}
        {data && <>
          <p className="font-mono-caps text-sm" data-testid="daily-login-progress">{days} / 7 days</p>
          <ol className="grid grid-cols-7 gap-2" aria-label="Progress toward your next bonus puzzle">
            {Array.from({ length: 7 }, (_, index) => <li key={index} className={`text-center border py-2 text-sm ${index < days ? "border-primary bg-primary/15 text-primary" : "border-white/20 text-muted-foreground"}`} aria-label={`Day ${index + 1}${index < days ? " checked in" : " remaining"}`}>{index + 1}</li>)}
          </ol>
          {days === 7 && <p className="text-primary" data-testid="daily-login-earned">Seven days! You earned a bonus puzzle. Your next seven-day run starts with tomorrow's check-in.</p>}
          {pending && <p className="text-sm text-muted-foreground">Your earned reward is saved. Admin has not designated an available bonus puzzle yet.</p>}
          {earnedPuzzles(rewards).length > 0 && <div className="space-y-2">
            <h3 className="font-mono-caps text-xs">YOUR BONUS PUZZLES</h3>
            {earnedPuzzles(rewards).map(reward => <Link key={reward.puzzle_id} to={`/play/${encodeURIComponent(reward.zone)}?puzzle_id=${encodeURIComponent(reward.puzzle_id)}`} onClick={() => setOpen(false)} className="block border border-primary/40 px-3 py-2 text-sm text-primary hover:bg-primary/10" data-testid={`login-bonus-${reward.puzzle_id}`}>{reward.title} — Play</Link>)}
          </div>}
        </>}
        <button type="button" onClick={() => setOpen(false)} className="border border-white/20 py-2 text-sm">Continue</button>
      </DialogContent>
    </Dialog>
  );
}
